import {
  ApiProject,
  ApiProjectSubscriptionStatus,
  ApiSimpleProjectSubscription,
  ApiSubscriptionStatus,
} from "@incendium/api";
import { Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { initializeClientsThunk } from "Actions";
import CopyClipboardBtn from "Components/CopyClipboardBtn/CopyClipboardBtn";
import GlassCard from "Components/GlassCard/GlassCard";
import Loading from "Components/Loading/Loading";
import SavingButton from "Components/UI/SavingButton";
import SpacedList from "Components/UI/SpacedList";
import { StyledDrawerContainer } from "Components/UI/StyledDrawer";
import { cell1Icon } from "consts";
import { PlatformURL } from "config";
import { AllProjectsSelector } from "features/project";
import useAllProjects from "features/project/hooks/useAllProjects";
import {
  createProjectSubscription,
  deleteProjectSubscription,
  useSubscriptions,
} from "features/subscription";
import useProjectSubscriptions from "features/subscription/hooks/useProjectSubscriptions";
import { sortNum } from "Helpers/arrays";
import { friendlyDateTime } from "Helpers/dates";
import withInfoColumn from "HoC/withInfoColumn";
import { useNotification } from "Hooks";
import { useConfirmation } from "Hooks/useConfirmation";
import { useSave } from "Hooks/useSave";
import produce from "immer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import { clientListSelector } from "Selectors/clientListSelector";

interface IProjectSubscriptionsPageInnerProps {
  projectSubscriptions: ApiSimpleProjectSubscription[];
  projects: ApiProject[];
  onDelete: (ps: ApiSimpleProjectSubscription) => void;
}

function ProjectSubscriptionsPageInner({
  projectSubscriptions,
  projects,
  onDelete,
}: IProjectSubscriptionsPageInnerProps) {
  const link = useCallback(
    (id) => {
      const project = projects.find((p) => p.id === id);
      if (!project) {
        return "";
      }
      return `${PlatformURL}/clients/${project.clientId}/projects/${project.id}/account/payment`;
    },
    [projects],
  );
  return (
    <SpacedList title="Project Subscriptions List">
      <TableHead>
        <TableRow>
          <TableCell>Subscription</TableCell>
          <TableCell>Project</TableCell>
          <TableCell>Link</TableCell>
          <TableCell>External</TableCell>
          <TableCell>Last Seen</TableCell>
          <TableCell>Status</TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {projectSubscriptions.map((ps) => (
          <TableRow key={ps.id}>
            <TableCell>{ps.subscription?.name}</TableCell>
            <TableCell>{ps.project?.name}</TableCell>

            <TableCell>
              <Stack direction={"row"} spacing={1} alignItems="center">
                <CopyClipboardBtn
                  isIconButton
                  variant="text"
                  text={link(ps.project?.id)}
                  size="extraSmall"
                />
                <Typography variant="body2">{link(ps.project?.id)}</Typography>
              </Stack>
            </TableCell>
            <TableCell>{ps.external ? "Yes" : "No"}</TableCell>
            <TableCell>
              {ps.seenAt ? friendlyDateTime(ps.seenAt) : "Not Seen Yet"}
            </TableCell>
            <TableCell>
              {ps.status ===
              ApiProjectSubscriptionStatus.PROJECT_SUBSCRIPTION_STATUS_PAID
                ? "Paid"
                : ps.seenAt
                  ? "Seen"
                  : "Created"}
            </TableCell>
            <TableCell align="right" sx={{ width: cell1Icon }}>
              {ps.status !==
                ApiProjectSubscriptionStatus.PROJECT_SUBSCRIPTION_STATUS_PAID && (
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => onDelete(ps)}
                >
                  <Delete />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </SpacedList>
  );
}

const ProjectSubscriptionsPageWithInfo = withInfoColumn(
  ProjectSubscriptionsPageInner,
);

function ProjectSubscriptionsPage() {
  const { projectSubscriptions, setProjectSubscriptions } =
    useProjectSubscriptions();
  const { subscriptions, loading: sLoading } = useSubscriptions();
  const [projectId, setProjectId] = useState<number | null>(null);
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const { saving, save } = useSave();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const handleDelete = useConfirmation();
  const [projects, setProjects] = useState<ApiProject[]>([]);
  const clients = useSelector(clientListSelector);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const { allProjects, fetched } = useAllProjects();
  const [trialDays, setTrialDays] = useState<number | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [isExternal, setIsExternal] = useState(false);

  // Initialize clients on mount
  useEffect(() => {
    dispatch(initializeClientsThunk());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Process projects when both clients and projects data are available
  useEffect(() => {
    if (!clients?.hasLoaded || !fetched) {
      return;
    }

    setProjects(
      sortNum(
        allProjects.map((p) => ({
          ...p,
          client: {
            id: p.clientId,
            businessName:
              clients.list.find((c) => c.id === p.clientId)?.businessName || "",
          },
        })),
        "clientId",
      ),
    );
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clients?.hasLoaded, allProjects, fetched]); // Only depend on hasLoaded, not the entire clients object

  const onSubmit = useCallback(() => {
    if (!projectId || !subscriptionId) {
      return;
    }
    try {
      save(async () => {
        const project = projects.find((p) => p.id === projectId);
        const res = await createProjectSubscription(
          projectId,
          subscriptionId,
          `${PlatformURL}/clients/${project?.clientId}/projects/${projectId}/account/payment?success=true`,
          true,
          trialDays || undefined,
          isAnnual,
          isExternal,
        );
        setProjectId(null);
        setSubscriptionId(null);
        setTrialDays(null);
        setIsAnnual(false);
        setProjectSubscriptions((cont) =>
          produce(cont, (draft) => {
            const idx = draft.findIndex((d) => d.id === res.id);
            if (idx >= 0) {
              draft[idx] = res;
            } else {
              draft.push(res);
            }
          }),
        );
        showSuccessNotification("Project Subscription Saved");
      });
    } catch (error) {
      showErrorNotification(
        "Failed to save Project Subscription, please try again",
      );
    }
  }, [
    save,
    projectId,
    subscriptionId,
    showSuccessNotification,
    showErrorNotification,
    setProjectSubscriptions,
    projects,
    trialDays,
    isAnnual,
    isExternal,
  ]);

  const selectedProject = useMemo(() => {
    return projects.find((p) => p.id === projectId);
  }, [projects, projectId]);

  const selectedSubscription = useMemo(() => {
    return subscriptions.find((p) => p.id === subscriptionId);
  }, [subscriptions, subscriptionId]);

  const activeSubscriptions = useMemo(() => {
    return subscriptions.filter(
      (s) => s.status === ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE,
    );
  }, [subscriptions]);

  const onSetProject = useCallback((p) => {
    setProjectId(p.id);
  }, []);

  const onDelete = useCallback(
    (ps: ApiSimpleProjectSubscription) => {
      handleDelete({
        title: "Are you sure you want to delete this project subscription",
        body: "This action cannot be undone.",
        callback: async () => {
          await deleteProjectSubscription(ps.id || "");
          setProjectSubscriptions((subs) =>
            produce(subs, (draft) => {
              const idx = draft.findIndex((d) => d.id === ps.id);
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            }),
          );
          return "Project Subscription Deleted";
        },
      });
    },
    [handleDelete, setProjectSubscriptions],
  );

  if (loading || sLoading) {
    return <Loading fullHeight />;
  }

  return (
    <ProjectSubscriptionsPageWithInfo
      projectSubscriptions={projectSubscriptions}
      projects={projects}
      onDelete={onDelete}
      infoColumn={
        <ValidatorForm onSubmit={onSubmit}>
          <StyledDrawerContainer>
            <GlassCard noShadow>
              <Stack spacing={2}>
                <Typography variant="subtitle1">
                  Create New Project Subscription
                </Typography>

                <AllProjectsSelector
                  project={selectedProject || null}
                  setProject={onSetProject}
                  variant="outlined"
                />
                <Autocomplete
                  id="subscription-picker"
                  size="small"
                  onChange={(e, value) => {
                    setSubscriptionId(value.id || null);
                    setIsAnnual(false);
                  }}
                  disableClearable
                  value={selectedSubscription || {}}
                  isOptionEqualToValue={(option, value) => {
                    return option?.id === value?.id;
                  }}
                  options={activeSubscriptions}
                  loading={sLoading}
                  getOptionLabel={(o) => o.name || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select a subscription"
                      variant="outlined"
                    />
                  )}
                />
                <TextField
                  label="Trial Days (optional)"
                  type="number"
                  value={trialDays}
                  size="small"
                  onChange={(e) => setTrialDays(Number(e.target.value) || null)}
                />

                {(selectedSubscription?.annualPrice || 0) > 0 && (
                  <FormControlLabel
                    label="Use annual pricing"
                    control={
                      <Switch
                        checked={isAnnual}
                        onChange={(e) => setIsAnnual(e.target.checked)}
                      />
                    }
                  />
                )}

                <FormControlLabel
                  label="Is Managed Externally?"
                  control={
                    <Switch
                      checked={isExternal}
                      onChange={(e) => setIsExternal(e.target.checked)}
                    />
                  }
                />
              </Stack>

              <Stack
                mt={4}
                direction={"row"}
                spacing={2}
                justifyContent="flex-end"
              >
                <Button color="secondary">Cancel</Button>
                <SavingButton
                  variant="contained"
                  color="primary"
                  type="submit"
                  saving={saving}
                  disabled={!projectId || !subscriptionId}
                >
                  Save
                </SavingButton>
              </Stack>
            </GlassCard>
          </StyledDrawerContainer>
        </ValidatorForm>
      }
    />
  );
}

export default ProjectSubscriptionsPage;
